import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { AdminLayout, ProtectedRoute, RequireAdmin } from "@/components/layout/AdminLayout";
import Home from "@/pages/public/Home";
import Rooms from "@/pages/public/Rooms";
import RoomDetail from "@/pages/public/RoomDetail";
import Prices from "@/pages/public/Prices";
import Booking from "@/pages/public/Booking";
import Arranger from "@/pages/public/Arranger";
import CalendarPage from "@/pages/public/CalendarPage";
import Events, { EventDetail } from "@/pages/public/Events";
import About from "@/pages/public/About";
import History from "@/pages/public/History";
import News from "@/pages/public/News";
import NewsPost from "@/pages/public/NewsPost";
import NewsletterUnsubscribe from "@/pages/public/NewsletterUnsubscribe";
import AdminPosts from "@/pages/admin/AdminPosts";
import AdminPostEditor from "@/pages/admin/AdminPostEditor";
import AdminStats from "@/pages/admin/AdminStats";
import AdminNewsletter from "@/pages/admin/AdminNewsletter";
import Area from "@/pages/public/Area";
import Contact from "@/pages/public/Contact";
import NotFound from "@/pages/public/NotFound";
import TicketPage from "@/pages/public/TicketPage";
import ChatPage from "@/pages/public/ChatPage";
import Velforeininga from "@/pages/public/Velforeininga";
import OrganizerPortal from "@/pages/organizer/OrganizerPortal";
import AboutSite from "@/pages/public/AboutSite";
import AdminTickets from "@/pages/admin/AdminTickets";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import Login from "@/pages/admin/Login";
import Dashboard from "@/pages/admin/Dashboard";
import Bookings from "@/pages/admin/Bookings";
import AdminCalendar from "@/pages/admin/AdminCalendar";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminRooms from "@/pages/admin/AdminRooms";
import AdminContent from "@/pages/admin/AdminContent";
import AdminPeople from "@/pages/admin/AdminPeople";
import AdminImages from "@/pages/admin/AdminImages";
import AdminPhotoComments from "@/pages/admin/AdminPhotoComments";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminMessages from "@/pages/admin/AdminMessages";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
        return;
      }
    }
    window.scrollTo({ top: 0 });
  }, [pathname, hash]);
  return null;
};

/** Old /booking links (e-post, SoMe, bokmerke) land on the new address with query intact. */
const LegacyBookingRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/leig-rom${search}`} replace />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/rom" element={<Rooms />} />
              <Route path="/rom/:slug" element={<RoomDetail />} />
              <Route path="/prisar" element={<Prices />} />
              <Route path="/leig-rom" element={<Booking />} />
              <Route path="/booking" element={<LegacyBookingRedirect />} />
              <Route path="/arranger" element={<Arranger />} />
              <Route path="/arranger/soknad" element={<Booking />} />
              <Route path="/kalender" element={<CalendarPage />} />
              <Route path="/arrangement" element={<Events />} />
              <Route path="/arrangement/:id" element={<EventDetail />} />
              <Route path="/om-oss" element={<About />} />
              <Route path="/historie" element={<History />} />
              <Route path="/velforeininga" element={<Velforeininga />} />
              <Route path="/omradet" element={<Area />} />
              <Route path="/kontakt" element={<Contact />} />
              <Route path="/om-nettsida" element={<AboutSite />} />
              <Route path="/nytt" element={<News />} />
              <Route path="/nytt/:slug" element={<NewsPost />} />
              <Route path="/nyheitsbrev/avmelding/:token" element={<NewsletterUnsubscribe />} />
              <Route path="/billett/:token" element={<TicketPage />} />
              <Route path="/spor-oss" element={<ChatPage />} />
              <Route path="/husvert" element={<Navigate to="/spor-oss" replace />} />
              <Route path="*" element={<NotFound />} />
            </Route>
            <Route path="/program" element={<Navigate to="/arrangement" replace />} />
            <Route path="/arrangor" element={<OrganizerPortal />} />
            <Route path="/arrangor/:token" element={<OrganizerPortal />} />
            <Route path="/arrangor/:token/:eventId" element={<OrganizerPortal />} />
            <Route path="/admin/login" element={<Login />} />
            <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="bookingar" element={<Bookings />} />
              <Route path="meldingar" element={<AdminMessages />} />
              <Route path="kalender" element={<AdminCalendar />} />
              <Route path="arrangement" element={<AdminEvents />} />
              <Route path="billettar" element={<AdminTickets />} />
              <Route path="nytt" element={<AdminPosts />} />
              <Route path="nytt/:id" element={<AdminPostEditor />} />
              <Route path="statistikk" element={<AdminStats />} />
              <Route path="nyheitsbrev" element={<AdminNewsletter />} />
              <Route path="integrasjonar" element={<RequireAdmin><AdminIntegrations /></RequireAdmin>} />
              <Route path="rom" element={<RequireAdmin><AdminRooms /></RequireAdmin>} />
              <Route path="innhald" element={<RequireAdmin><AdminContent /></RequireAdmin>} />
              <Route path="folk" element={<RequireAdmin><AdminPeople /></RequireAdmin>} />
              <Route path="bilete" element={<AdminImages />} />
              <Route path="bildekommentarar" element={<RequireAdmin><AdminPhotoComments /></RequireAdmin>} />
              <Route path="innstillingar" element={<AdminSettings />} />
            </Route>
          </Routes>
          <Toaster position="top-right" closeButton />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
